import React, { useState } from 'react';
import { Zap } from 'lucide-react';
import { formatNumber } from '@/utils/formatNumber';
import { IconTitle } from '@/components/ui/icon-title';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';

interface EnergyConsumptionCardProps {
  acConsumption: number | null | undefined;
  otherConsumption: number | null | undefined;
  solarProduced: number | null | undefined;
  gridImport: number | null | undefined;
  todayEnergy?: number | null | undefined;
}

const EnergyConsumptionCard: React.FC<EnergyConsumptionCardProps> = ({
  acConsumption,
  otherConsumption,
  solarProduced,
  gridImport,
  todayEnergy = 0,
}) => {

  // State for legend visibility
  const [sourcesLegendVisible, setSourcesLegendVisible] = useState({
    solar: true,
    grid: true
  });

  const [usageLegendVisible, setUsageLegendVisible] = useState({
    ac: true,
    other: true
  });

  // Handle null/undefined values for calculations
  const safeValues = {
    solarProduced: solarProduced ?? 0,
    gridImport: gridImport ?? 0,
    acConsumption: acConsumption ?? 0,
    otherConsumption: otherConsumption ?? 0,
    todayEnergy: todayEnergy ?? 0,
  };
  
  // Energy Sources Chart Data
  const energySourcesData = [
    { 
      name: 'Solar', 
      value: solarProduced !== null ? Math.round(solarProduced) : 0, 
      color: '#F2AA32',
      visible: sourcesLegendVisible.solar
    },
    { 
      name: 'Grid', 
      value: gridImport !== null ? Math.round(gridImport) : 0, 
      color: '#7D3BBF',
      visible: sourcesLegendVisible.grid
    }
  ].filter(item => item.visible);

  // Usage Breakdown Chart Data
  const usageBreakdownData = [
    { 
      name: 'A/C', 
      value: acConsumption !== null ? Math.round(acConsumption) : 0, 
      color: '#0E7EE4',
      visible: usageLegendVisible.ac
    },
    { 
      name: 'Other', 
      value: otherConsumption !== null ? Math.round(otherConsumption) : 0, 
      color: '#9CA3AF',
      visible: usageLegendVisible.other
    }
  ].filter(item => item.visible);

  // Add fallback data if all values are 0 or arrays are empty
  const hasSourceData = energySourcesData.length > 0 && energySourcesData.some(item => item.value > 0);
  const hasUsageData = usageBreakdownData.length > 0 && usageBreakdownData.some(item => item.value > 0);
  
  // Use placeholder data if no real data
  const displaySourcesData = hasSourceData ? energySourcesData : [
    { name: 'No Data', value: 1, color: '#E5E7EB' }
  ];
  
  const displayUsageData = hasUsageData ? usageBreakdownData : [
    { name: 'No Data', value: 1, color: '#E5E7EB' }
  ];

  // Debug logging
  console.log('Energy Consumption Card Data:', {
    props: { acConsumption, otherConsumption, solarProduced, gridImport },
    energySourcesData,
    displaySourcesData,
    usageBreakdownData,
    displayUsageData,
    hasSourceData,
    hasUsageData
  });

  return (
    <div className="bg-white rounded-lg border border-light-grey p-3 pt-2 h-full flex flex-col">
      {/* Energy Consumption Title */}
      <div className="mb-3">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <IconTitle icon={Zap} title="Energy Consumption" />
            <span className="text-[8px] text-dark-grey bg-gray-100 px-1.5 py-0.5 rounded">Today</span>
          </div>
          <div className="flex items-baseline gap-1">
            <div className="flex items-baseline gap-1 bg-gray-100 px-1.5 py-0.5 rounded">
              <p className="heading-6 text-black">{formatNumber(todayEnergy, 0)}</p>
              <span className="paragraph-12 text-black">kWh</span>
            </div>
          </div>
        </div>
      </div>

      {/* Energy Data Section */}
      <div className="flex-1 space-y-3">

        {/* Energy Sources */}
        <div>
          <h4 className="text-[10px] text-dark-grey font-medium tracking-wide">Sources</h4>
          <div className="flex items-center">
            {/* Grid - Left Edge */}
            <div className="flex-1 flex flex-col justify-center">
              <div 
                className={`flex items-center gap-1.5 mb-1 cursor-pointer transition-opacity ${!sourcesLegendVisible.grid ? 'opacity-50' : ''}`}
                onClick={() => setSourcesLegendVisible(prev => ({ ...prev, grid: !prev.grid }))}
              >
                <div className="w-2.5 h-2.5 rounded shadow-sm" style={{backgroundColor: '#7D3BBF'}}></div>
                <span className="paragraph-12-medium text-black">Grid</span>
              </div>
              <div className="heading-6 text-black">{formatNumber(gridImport, 0)}</div>
              <div className="text-[10px] text-dark-grey mb-1">kWh</div>
              <div className="flex justify-start">
                <div className="inline-flex items-center px-1.5 py-0.5 border border-gray-200 rounded-md">
                  <span className="text-[11px] font-medium text-gray-700">
                    {(solarProduced !== null && gridImport !== null && (solarProduced + gridImport) > 0 && hasSourceData)
                      ? formatNumber((gridImport / (solarProduced + gridImport)) * 100, 0) 
                      : '-'}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Donut Chart - Center */}
            <div className="flex-shrink-0 w-2/5 h-24 sm:h-28 md:h-32 lg:h-36 mx-2 sm:mx-3 md:mx-4 lg:mx-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={displaySourcesData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                  >
                    {displaySourcesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Solar - Right Edge */}
            <div className="flex-1 text-right flex flex-col justify-center">
              <div 
                className={`flex items-center gap-1.5 justify-end mb-1 cursor-pointer transition-opacity ${!sourcesLegendVisible.solar ? 'opacity-50' : ''}`}
                onClick={() => setSourcesLegendVisible(prev => ({ ...prev, solar: !prev.solar }))}
              >
                <span className="paragraph-12-medium text-black">Solar</span>
                <div className="w-2.5 h-2.5 rounded shadow-sm" style={{backgroundColor: '#F2AA32'}}></div>
              </div>
              <div className="heading-6 text-black text-right">{formatNumber(solarProduced, 0)}</div>
              <div className="text-[10px] text-dark-grey mb-1 text-right">kWh</div>
              <div className="flex justify-end">
                <div className="inline-flex items-center px-1.5 py-0.5 border border-gray-200 rounded-md">
                  <span className="text-[11px] font-medium text-gray-700">
                    {(solarProduced !== null && gridImport !== null && (solarProduced + gridImport) > 0 && hasSourceData)
                      ? formatNumber((solarProduced / (solarProduced + gridImport)) * 100, 0) 
                      : '-'}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Breakdown */}
        <div>
          <h4 className="text-[10px] text-dark-grey font-medium tracking-wide">Usage</h4>
          <div className="flex items-center">
            {/* A/C - Left Edge */}
            <div className="flex-1 flex flex-col justify-center">
              <div 
                className={`flex items-center gap-1.5 mb-1 cursor-pointer transition-opacity ${!usageLegendVisible.ac ? 'opacity-50' : ''}`}
                onClick={() => setUsageLegendVisible(prev => ({ ...prev, ac: !prev.ac }))}
              >
                <div className="w-2.5 h-2.5 rounded shadow-sm" style={{backgroundColor: '#0E7EE4'}}></div>
                <span className="paragraph-12-medium text-black">A/C</span>
              </div>
              <div className="heading-6 text-black">{formatNumber(acConsumption, 0)}</div>
              <div className="text-[10px] text-dark-grey mb-1">kWh</div>
              <div className="flex justify-start">
                <div className="inline-flex items-center px-1.5 py-0.5 border border-gray-200 rounded-md">
                  <span className="text-[11px] font-medium text-gray-700">
                    {(acConsumption !== null && otherConsumption !== null && (acConsumption + otherConsumption) > 0 && hasUsageData)
                      ? formatNumber((acConsumption / (acConsumption + otherConsumption)) * 100, 0) 
                      : '-'}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Donut Chart - Center */}
            <div className="flex-shrink-0 w-2/5 h-24 sm:h-28 md:h-32 lg:h-36 mx-2 sm:mx-3 md:mx-4 lg:mx-6">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={displayUsageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    startAngle={90}
                    endAngle={-270}
                    dataKey="value"
                  >
                    {displayUsageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Other - Right Edge */}
            <div className="flex-1 text-right flex flex-col justify-center">
              <div 
                className={`flex items-center gap-1.5 justify-end mb-1 cursor-pointer transition-opacity ${!usageLegendVisible.other ? 'opacity-50' : ''}`}
                onClick={() => setUsageLegendVisible(prev => ({ ...prev, other: !prev.other }))}
              >
                <span className="paragraph-12-medium text-black">Other</span>
                <div className="w-2.5 h-2.5 rounded shadow-sm" style={{backgroundColor: '#9CA3AF'}}></div>
              </div>
              <div className="heading-6 text-black text-right">{formatNumber(otherConsumption, 0)}</div>
              <div className="text-[10px] text-dark-grey mb-1 text-right">kWh</div>
              <div className="flex justify-end">
                <div className="inline-flex items-center px-1.5 py-0.5 border border-gray-200 rounded-md">
                  <span className="text-[11px] font-medium text-gray-700">
                    {(acConsumption !== null && otherConsumption !== null && (acConsumption + otherConsumption) > 0 && hasUsageData)
                      ? formatNumber((otherConsumption / (acConsumption + otherConsumption)) * 100, 0) 
                      : '-'}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnergyConsumptionCard;